import { NewAnalysisInvestmentProjectData } from '@/app/store/new-investment-analysis-store';
import { Agent, ISubscriptionResponse } from '@/types/general';
import { QueryClient } from '@tanstack/react-query';
import clsx, { type ClassValue } from 'clsx';
import { Session } from 'next-auth';
import { twMerge } from 'tailwind-merge';

export const cn = (...inputs: ClassValue[]) => {
  return twMerge(clsx(inputs));
};

export const hasAccess = (agent: Agent, session: Session | null) =>
  session?.user?.agentAccess?.includes(agent);

export const createQueryString = (name: string, value: string) => {
  const params = new URLSearchParams();
  params.set(name, value);

  return params.toString();
};

export const queryClient = new QueryClient();

export const getSubscriptionStatusValues = (
  subscription: ISubscriptionResponse
) => {
  if (subscription) {
    // 1. Parse dates from the subscription object
    const startDate = new Date(subscription.start_date);
    const endDate = new Date(subscription.end_date);
    const today = new Date();

    // 2. Format the end date into a more readable string
    // e.g., "Friday, 13 July 2025"
    const formattedEndDate = new Intl.DateTimeFormat('en-GB', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(endDate);

    // 3. Calculate the difference in days between today and the end date
    // Math.ceil rounds up to the nearest whole number to be more user-friendly
    const daysRemaining = Math.ceil(
      (endDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
    );
    const daysRemainingText =
      daysRemaining > 0
        ? `${daysRemaining} day(s).`
        : 'Your subscription has expired.';

    // 4. Calculate the percentage of the subscription period covered
    const totalDuration = endDate.getTime() - startDate.getTime();
    const coveredDuration = today.getTime() - startDate.getTime();

    // Ensure we don't divide by zero if start and end dates are the same
    let percentageCovered = 0;
    if (totalDuration > 0) {
      // Clamp the percentage between 0 and 100
      percentageCovered = Math.max(
        0,
        Math.min(100, (coveredDuration / totalDuration) * 100)
      );
    } else if (today >= endDate) {
      percentageCovered = 100;
    }

    // 5. Construct the final output
    return {
      formattedEndDate,
      daysRemainingText,
      daysRemaining,
      percentageCovered: percentageCovered.toFixed(2),
    };
  }
  return null;
};

export const getColorByLabel = (label: string) => {
  switch (label) {
    case 'High Priority':
      return '#B42318';
    case 'Critical Priority':
      return '#B42318';
    case 'Medium Priority':
      return '#DC6803';
    case 'Resolved':
      return '#027A48';
    default:
      return '#111827';
  }
};

export const getPriorityStyle = (priority: string) => {
  const normalized = priority.trim().toLowerCase();
  switch (normalized) {
    case 'high':
      return {
        text: 'text-[#E11D48]',
        dot: 'bg-[#E11D48]',
        bg: 'bg-[#FFF1F2]',
      };
    case 'medium':
      return {
        text: 'text-[#3A7BAA]',
        dot: 'bg-[#3A7BAA]',
        bg: 'bg-[#DDF1FF]',
      };
    case 'low':
      return {
        text: 'text-[#DC6803]',
        dot: 'bg-red-700',
        bg: 'bg-[#FFFAEB]',
      };
    default:
      return {
        text: 'text-gray-700',
        dot: 'bg-gray-400',
        bg: 'bg-gray-100',
      };
  }
};

export function formatDateWithTime(date: string | Date): string {
  const d = new Date(date);

  const options: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  };

  const parts = new Intl.DateTimeFormat('en-US', options).formatToParts(d);

  const month = parts.find((p) => p.type === 'month')?.value;
  const day = parts.find((p) => p.type === 'day')?.value;
  const year = parts.find((p) => p.type === 'year')?.value;
  const hour = parts.find((p) => p.type === 'hour')?.value;
  const minute = parts.find((p) => p.type === 'minute')?.value;
  const dayPeriod = parts.find((p) => p.type === 'dayPeriod')?.value;

  return `${month} ${day}, ${year} @${hour}:${minute} ${dayPeriod}`;
}

export function capitalize(word: string): string {
  if (!word) return '';
  return word.charAt(0).toUpperCase() + word.slice(1);
}

export const timelineMultiplier = (
  timeline: NewAnalysisInvestmentProjectData['project_timeline']
): NewAnalysisInvestmentProjectData['project_timeline'] => {
  if (!timeline) return timeline;

  return {
    ...timeline,
    construction_period_unit: 'Months',
    stabilization_period_unit: 'Months',
    adu_construction_period_unit: 'Months',

    // construction_period_months: timeline.construction_period_months * 12,
    // stabilization_period_months: timeline.stabilization_period_months * 12,
    // adu_construction_period_months:
    //   timeline.adu_construction_period_months * 12,
  };
};

export const refinancingDetailsMultiplier = (
  refinancing: NewAnalysisInvestmentProjectData['refinancing_details']
): NewAnalysisInvestmentProjectData['refinancing_details'] => {
  if (!refinancing) return refinancing;

  return {
    ...refinancing,
    new_loan_term_unit: 'Months',
    // new_loan_term_months: refinancing.new_loan_term_months * 12,
  };
};

export const financingStructureMultiplier = (
  finStrucV2: NewAnalysisInvestmentProjectData['financing_structure_v2']
): NewAnalysisInvestmentProjectData['financing_structure_v2'] => {
  if (!finStrucV2) return finStrucV2;

  return {
    ...finStrucV2,
    first_loan_details: finStrucV2.first_loan_details
      ? {
          ...finStrucV2.first_loan_details,
          loan_term_unit: 'Months',
          // loan_term_months: finStrucV2.first_loan_details.loan_term_months * 12,
        }
      : finStrucV2.first_loan_details,
    second_loan_details: finStrucV2.second_loan_details
      ? {
          ...finStrucV2.second_loan_details,
          loan_term_unit: 'Months',
          // loan_term_months:
          // finStrucV2.second_loan_details.loan_term_months * 12,
        }
      : finStrucV2.second_loan_details,
  };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const deepMerge = (target: any, source: any): any => {
  if (typeof target !== 'object' || target === null) return source;
  if (typeof source !== 'object' || source === null) return source;

  const merged = { ...target };
  for (const key of Object.keys(source)) {
    merged[key] = deepMerge(target[key], source[key]);
  }
  return merged;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const updateByPath = <T extends Record<string, any>>(
  obj: T,
  path: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any
): T => {
  const keys = path.split('.');
  const lastKey = keys.pop() as string;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const nested: any = { ...obj };
  let current = nested;

  for (const key of keys) {
    current[key] = { ...(current[key] || {}) };
    current = current[key];
  }

  current[lastKey] = value;
  return nested;
};

export const formatDate = (iso: string | undefined) => {
  if (!iso) return 'Missing data';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return 'Missing data';
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

type CalculatorField = {
  name: string;
  label: string;
  value: number;
};

const humanize = (value: string) =>
  value
    .replace(/_/g, ' ')
    .replace(/\./g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

export const generateReadableLabel = (prefix: string, key: string) => {
  return humanize(key);
};

export const buildCalculatorFieldsFromProject = (
  project: NewAnalysisInvestmentProjectData,
  fieldLabels: Record<string, string> = {}
): CalculatorField[] => {
  const fields: CalculatorField[] = [];

  const flatMappings: [string, unknown][] = [
    ['about_property', project?.about_property],
    ['construction_budget', project?.construction_budget],
    ['project_timeline', project?.project_timeline],
    [
      'financing_structure_v2.first_loan_details',
      project?.financing_structure_v2?.first_loan_details,
    ],
    [
      'financing_structure_v2.second_loan_details',
      project?.financing_structure_v2?.second_loan_details,
    ],
    ['refinancing_details', project?.refinancing_details],
    ['operating_assumptions', project?.operating_assumptions],
    ['exit_strategy', project?.exit_strategy],
    ['investment_structure', project?.investment_structure],
  ];

  for (const [prefix, section] of flatMappings) {
    if (!section || typeof section !== 'object') continue;

    for (const [key, value] of Object.entries(section)) {
      if (typeof value === 'number' && !Number.isNaN(value)) {
        const fullKey = `${prefix}.${key}`;

        fields.push({
          name: fullKey,
          value,
          label: fieldLabels[fullKey] ?? generateReadableLabel(prefix, key),
        });
      }
    }
  }

  if (project) {
    const totalCost =
      (project?.about_property?.purchase_price ?? 0) +
      (project?.construction_budget?.adu_budget ?? 0) +
      (project?.construction_budget?.soft_cost ?? 0) +
      (project?.construction_budget?.hard_cost ?? 0) +
      0.04 * (project?.about_property?.purchase_price ?? 0);

    fields.push({
      name: 'derived.total_cost',
      label: 'Total Project Cost',
      value: totalCost,
    });
  }

  return fields;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const formatItemsForBackend = <T extends { type?: string; value: any }>(
  items: T[]
) =>
  items.map((item) => {
    if (item.type !== 'percentage') return item;
    return { ...item, value: Number(item.value) / 100 };
  });

export const formatInputsNumber = (value: number, decimals = 0) =>
  new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
